import React, { useEffect, useState } from 'react';
import { getService } from '@Client/runner.hooks/useService';
import { Guid } from '@Shared.Angular/@types/guid';
import { IProcedureSchemaVersion } from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureSchemaVersion';
import { Spinner } from '@Shared.Angular/flowingly.components/Spinner/Spinner';
import {
  VersionHistoryItem,
  IVersionHistoryItem
} from '@Shared.Angular/flowingly.components/VersionHistory/VersionHistoryItem';
import Modal from '@Shared.Angular/flowingly.components/Modal/Modal';
import { ProcedureViewMode } from '@Shared.Angular/@types/core/contracts/constants/procedureViewMode';
import { VersionType } from '@Shared.Angular/interfaces/version-type.enum';
import { VersionBadge } from '@Shared.Angular/flowingly.components/VersionHistory/VersionBadge';

type Props = {
  procedureId: Guid;
  onRestoreComplete?: () => void;
};

export const ProcedureVersionHistoryPanel = ({
  procedureId,
  onRestoreComplete
}: Props) => {
  const [versions, setVersions] = useState<IProcedureSchemaVersion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [schemaIdToRestore, setSchemaIdToRestore] = useState<Guid | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);

  const procedureApiService = getService('procedureApiService');
  const dateService = getService('dateService');
  const notificationService = getService('notificationService');

  const fetchVersions = () => {
    setIsLoading(true);
    setError(null);

    procedureApiService
      .getProcedureSchemaVersions(procedureId)
      .then((data: IProcedureSchemaVersion[]) => {
        setVersions(data);
        setIsLoading(false);
      })
      .catch(() => {
        setError('Failed to load version history');
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchVersions();
  }, [procedureId]);

  const formatVersionLabel = (version: IProcedureSchemaVersion): string => {
    return `V${version.versionNumber}`;
  };

  const buildVersionHistoryItem = (
    version: IProcedureSchemaVersion
  ): IVersionHistoryItem => {
    const versionLabel = formatVersionLabel(version);
    const versionNumber = version.versionNumber?.toString();

    return {
      title: dateService.utcToLocalCustom(
        version.publishDateUtc,
        'D MMMM YYYY'
      ),
      subtitle: `Published (${versionLabel})${
        version.updatedByName ? ` by ${version.updatedByName}` : ''
      }`,
      time: dateService.utcToLocalCustom(version.publishDateUtc, 'h:mm A'),
      badge: () => (
        <VersionBadge
          status={VersionType.Published}
          versionNumber={versionNumber}
        />
      )
    };
  };

  const openSchemaInNewTab = (schemaId: Guid) => {
    const url = `/sops/${procedureId}?viewMode=${ProcedureViewMode.ReadOnly}&schemaId=${schemaId}`;
    window.open(url, '_blank');
  };

  const confirmRestore = () => {
    if (!schemaIdToRestore) return;

    setIsRestoring(true);
    procedureApiService
      .restoreProcedureVersion(procedureId, schemaIdToRestore)
      .then(() => {
        notificationService.showSuccessToast('Version restored successfully');
        setSchemaIdToRestore(null);
        fetchVersions();
        onRestoreComplete?.();
      })
      .catch(() => {
        notificationService.showErrorToast('Failed to restore version');
      })
      .finally(() => {
        setIsRestoring(false);
      });
  };

  const cancelRestore = () => {
    setSchemaIdToRestore(null);
  };

  if (isLoading) {
    return (
      <div className="sop-version-history-panel">
        <div className="loading-state">
          <Spinner />
          <span>Loading version history...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sop-version-history-panel">
        <div className="error-state">
          <i className="fa-light fa-circle-exclamation"></i>
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="sop-version-history-panel">
        <div className="empty-state">
          <i className="fa-light fa-clock-rotate-left"></i>
          <span>No version history available</span>
        </div>
      </div>
    );
  }

  return (
    <div className="sop-version-history-panel">
      <div className="version-list">
        {versions.map((version) => (
          <VersionHistoryItem
            key={version.procedureSchemaId}
            versionHistoryItem={buildVersionHistoryItem(version)}
            onViewClick={() => openSchemaInNewTab(version.procedureSchemaId)}
            onRestoreClick={() =>
              setSchemaIdToRestore(version.procedureSchemaId)
            }
          />
        ))}
        <hr />
      </div>
      {schemaIdToRestore && (
        <Modal
          title="Restore Version"
          size="sm"
          onClose={cancelRestore}
          onCancelClick={cancelRestore}
          actionButtonText={isRestoring ? 'Restoring...' : 'Restore'}
          actionButtonType="primary"
          onActionClick={confirmRestore}
          isActionDisabled={isRestoring}
        >
          <p>
            Restoring this version will overwrite your current draft. Do you
            wish to proceed?
          </p>
        </Modal>
      )}
    </div>
  );
};
